// Ported from dev-scrips-pm-react scheduling/components/appointments/edit-recurring-scope-dialog.tsx (PROD-1190 calendar port)
/**
 * EditRecurringScopeDialog — scope picker for editing a recurring
 * appointment. Mirrors the cancel dialog's scope step (Flutter
 * `appointments_tab.dart:147-265` → "Reschedule this appointment" /
 * "Reschedule this and following" / "Reschedule all in series").
 *
 * Wire-shape contract (dev-curl verified 2026-05-13):
 *
 *   this-only   → editRecurring: null
 *   following   → editRecurring: {
 *                   appointmentDate: <source.start ISO>,
 *                   allAppointment: false,
 *                   dataUpdate: <changed-fields[]>
 *                 }
 *   all         → editRecurring: {
 *                   appointmentDate: null,
 *                   allAppointment: true,
 *                   dataUpdate: <changed-fields[]>
 *                 }
 *
 * Mutation-agnostic — the dialog returns the scope choice via
 * `onSelect`. The consumer builds the editRecurring payload (because
 * only the consumer knows which fields actually changed for
 * `dataUpdate`) and fires the PUT /api/Appointment/{id} call.
 *
 * Helper {@link buildEditRecurringPayload} translates scope + source
 * appointment + changed fields into the payload, in case the
 * consumer wants to skip that math.
 */

import { useEffect, useState } from 'react';
import { Button, Modal } from '@scripsteam/scrips-signal-ds';
import type { RecurringScope } from './cancel-appointment-dialog';

export type { RecurringScope };

interface EditRecurringScopeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Source occurrence's start ISO datetime — surfaces in the "this
   * and following" option label as a date hint.
   */
  occurrenceStartISO?: string;
  /**
   * Fired once the user picks a scope and confirms with Apply.
   * Dialog closes itself before firing — consumer doesn't need to.
   */
  onSelect: (scope: RecurringScope) => void;
}

const OPTIONS: Array<{
  value: RecurringScope;
  title: string;
  description: string;
}> = [
  {
    value: 'this-only',
    title: 'This appointment only',
    description: 'Only this occurrence changes; the rest of the series stays as booked.',
  },
  {
    value: 'following',
    title: 'This and following appointments',
    description: 'This occurrence and every later one in the series change.',
  },
  {
    value: 'all',
    title: 'All appointments in the series',
    description: 'Every occurrence in the series changes.',
  },
];

export function EditRecurringScopeDialog({
  open,
  onOpenChange,
  occurrenceStartISO,
  onSelect,
}: EditRecurringScopeDialogProps) {
  const [scope, setScope] = useState<RecurringScope | null>(null);

  useEffect(() => {
    if (open) setScope(null);
  }, [open]);

  const handleApply = () => {
    if (!scope) return;
    onOpenChange(false);
    onSelect(scope);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Reschedule recurring appointment"
      size="sm"
      footer={<><Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!scope}>
            Continue
          </Button></>}
    >
        

        {/* The design's scope cards (SchedDialogs `SchedScopeOptions`, "Apply changes to"); the PA's intro line and the
            "(from date)" hint on "following" are kept. */}
        <div className="booking-dialog-stack">
          <p className="booking-dialog-muted">
            This appointment is part of a recurring series. Choose how your
            changes should apply.
          </p>
          <div>
            <p className="booking-dialog-label">Apply changes to</p>
            <div className="booking-scopes">
              {OPTIONS.map((opt) => {
                const active = scope === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setScope(opt.value)}
                    className="booking-scope"
                    data-on={active || undefined}
                    aria-pressed={active}
                  >
                    <span className="booking-scope-label">
                      {opt.title}
                      {opt.value === 'following' && occurrenceStartISO ? (
                        <span className="booking-scope-desc"> (from {formatHint(occurrenceStartISO)})</span>
                      ) : null}
                    </span>
                    <span className="booking-scope-desc">{opt.description}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

export interface EditRecurringPayload {
  appointmentDate: string | null;
  allAppointment: boolean;
  /**
   * Field names that changed in this edit — backend uses this to decide
   * which columns to propagate across the series. Flutter sends e.g.
   * `["start", "end"]` when only time was rescheduled. The consumer
   * computes this by diffing the loaded appointment vs the new body.
   */
  dataUpdate: string[];
}

/**
 * Translates a scope choice + source occurrence + changed field names
 * into the `editRecurring` body field for PUT /api/Appointment/{id}.
 *
 * Returns `null` for the "this-only" scope — that's the wire shape
 * Flutter sends when no series-wide propagation is needed.
 */
export function buildEditRecurringPayload(
  scope: RecurringScope,
  occurrenceStartISO: string | undefined,
  dataUpdate: string[],
): EditRecurringPayload | null {
  if (scope === 'this-only') return null;
  if (scope === 'all') {
    return { appointmentDate: null, allAppointment: true, dataUpdate };
  }
  // 'following' — backend reads `appointmentDate` as the cutoff.
  return {
    appointmentDate: occurrenceStartISO ?? null,
    allAppointment: false,
    dataUpdate,
  };
}

function formatHint(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
